/*!
 * CGParse.js – Sequence & Feature Parser for CGView.js
 * Copyright © 2024–2025 Jason R. Grant
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// IMPROVE DOCS:
// - columns: array of column paths to search. if not set, all leaf columns will be searched
// - excludeColumns: array of column names to exclude from search
//   - If a column has sub data, it will be excluded as well
class CGSearch {
  constructor(data, options = {}) {
    this._data = data;
    this.options = options;
    this.debug = options.debug || false;
    this._columnPaths = this.options.columns || [];
    this._columnPathTypes = new Map();
    this.determineTypes = options.determineTypes || false;
    // Make accessors for all columns
    if (this._columnPaths.length === 0) {
      this._createColumnsAndAccessors();
    } else {
      this._expandAndSetColumns();
    }
    if (this.determineTypes) {
      this._columnPathTypes = this.getAllLeafColumnPathsWithTypes(this._data, this._columnPaths);
    }
  }

  get data() {
    return this._data;
  }
  set data(value) {
    this._data = value;
    if (!this.options.columns?.length) {
      this._createColumnsAndAccessors();
    } else {
      this._expandAndSetColumns();
    }
    if (this.determineTypes) {
      this._columnPathTypes = this.getAllLeafColumnPathsWithTypes(this._data, this._columnPaths);
    }
  }

  get columnPaths() {
    return this._columnPaths;
  }
  set columnPaths(value) {
    this._columnPaths = value;
    this.accessors = this.makeAccessorMap(this._columnPaths);
    if (this.determineTypes) {
      this._columnPathTypes = this.getAllLeafColumnPathsWithTypes(this._data, this._columnPaths);
    }
  }

  get columnPathTypes() {
    return this._columnPathTypes;
  }

  set columnPathTypes(value) {
    this._columnPathTypes = value;
  }

  _createColumnsAndAccessors() {
    const allPaths = this.getAllColumnPaths(this.data);
    this._columnPaths = this.filterExcludedColumns(allPaths, this.options.excludeColumns);
    this.accessors = this.makeAccessorMap(this._columnPaths);
  }

  filterExcludedColumns(columns, excluded) {
    if (!excluded || excluded.length === 0) return columns;
    return columns.filter(col => {
      // return !excluded.some(ex => col === ex || col.startsWith(ex + ':'));
      return !excluded.some(ex => col === ex || col.startsWith(ex.endsWith(':') ? ex : ex + ':'));
    });
  }

  sanitize(string) {
    // Get rid of decoration divs
    let sanitized = string.replace(/<div class="token-(column|modifier)".*?>.*?<\/div>/g, '');
    // Change nbsp to ' '
    sanitized = sanitized.replace(/&nbsp;/g, ' ');
    // Trim spaces from begining of string
    sanitized = sanitized.replace(/^\s+/, '');
    // Remove styles (e.g. @font-face). This needs to be done before removing all style tags
    sanitized = sanitized.replace(/<style>[\s\S]*<\/style>/g, '');
    // Remove all tags
    // sanitized = sanitized.replace(/<\/?[^>]+(>|$)/g, '');
    sanitized = sanitized.replace(/<\/?[^>]+>/g, '');
    return sanitized
  }

  columnTypeFor(column) {
    let columnType;
    if (column.trim() === ':' || column.trim() === '') {
      columnType = 'any';
    } else if (this.columnPaths.includes(column)) {
      columnType = column.endsWith(':') ? 'parent' : 'known';
    } else {
      columnType = 'unknown';
    }
    return columnType;
  }

  tokenize(string) {
    const sanitizedString = this.sanitize(string);
    // const columns = this.columnize(string);
    // const columns = []; // FIXME: temporary fix, no columns
    if (this.debug) { console.log(`${string} --> '${sanitizedString}'`); }
    const tokens = [];
    if (!sanitizedString || sanitizedString.trim() === '') {
      if (this.debug) { console.log("Empty string, returning empty tokens array"); }
      return tokens; // Return empty array if string is empty
    }

    // Split string by spaces.
    // Unless there are quotes (") 
    // Then group everything from one quote to the next or the end of the string
    // 'one two  three'         => ['one', 'two', 'three']
    // 'one "two  three"'       => ['one', '"two  three"']
    // 'one two  "three '       => ['one', 'two', 'three ']
    // 'one two  "three four" ' => ['one', 'two', 'three four', '']
    // const values = sanitizedString.match(/(?:[^\s"]+|"[^"]*"?|\s+$)+/g);
    const values = sanitizedString.match(/([^\s"]+|"[^"]*"?|\s+$)+/g);

    for (let [i, rawValue] of values.entries()) {

      // Column
      // let  [value, typedColumn] = rawValue.split(':');

      // | `rawValue`              | `value` | `typedColumn`       |
      // |-------------------------|--------|----------------------|
      // | `bob`                   | `bob`  | `undefined`          |
      // | `bob:meta`              | `bob`  | `meta`               |
      // | `bob:meta:sub1`         | `bob`  | `meta:sub1`          |
      // | `bob:meta:sub1:sub2`    | `bob`  | `meta:sub1:sub2`     |
      let [value, ...rest] = rawValue.split(':');
      let typedColumn = rest.length > 0 ? rest.join(':') : undefined;

      // let value = rawValue;
      // let typedColumn;

      // if (!/^".*"$/.test(rawValue)) {
      //   const parts = rawValue.split(':');
      //   if (parts.length > 1) {
      //     value = parts[0];
      //     typedColumn = parts.slice(1).join(':');
      //   }
      // }
      // value = value.replace(/^"|"$/g, '');

      // Remove quotes from value
      value = value.replace(/"/g, '');


      // let column = typedColumn || columns[i] || 'any'; // FIXME
      // Remove column name after ':', if it is a column name
      // if (this.columnMap(typedColumn)) {
      //   const re = new RegExp(`:${typedColumn}$`)
      //   rawValue = rawValue.replace(re, '');
      // }

      // Improved column normalization logic
      let column;
      if (!typedColumn || typedColumn.trim() === '' || typedColumn.trim() === ':') {
        // column = 'any';
        column = ':';
      } else if (this.columnPaths.includes(typedColumn)) {
        column = typedColumn;
      } else if (!typedColumn.endsWith(':') && this.columnPaths.includes(typedColumn + ':')) {
        // Normalize: treat 'meta' as 'meta:' if the latter exists
        column = typedColumn + ':';
      } else {
        column = `?:${typedColumn}`;
      }

      // Determine columnType
      let columnType = this.columnTypeFor(column);
      // let columnType;
      // // if (column === 'any') {
      // if (column === ':') {
      //   columnType = 'any';
      // } else if (this.columnPaths.includes(column)) {
      //   columnType = column.endsWith(':') ? 'parent' : 'known';
      // } else {
      //   columnType = 'unknown';
      // }

      // Logic
      let logic = 'or';
      if (value.startsWith('!')) {
        logic = 'not';
        value = value.replace(/^!/g, '');
      } else if (value.startsWith('+')) {
        logic = 'and';
        value = value.replace(/^\+/g, '');
      }

      // Modifiers
      let modifier;
      if (value.startsWith('*')) {
        modifier = 'ends';
        value = value.replace(/\*/g, '');
      } else if (value.endsWith('*')) {
        modifier = 'begins';
        value = value.replace(/\*/g, '');
      } else if (value.startsWith('=')) {
        modifier = 'eq';
        value = value.replace(/=/g, '');
      } else if (value.startsWith('&gt;=') || value.startsWith('>=')) {
        modifier = 'gte';
        value = value.replace(/(>|&gt;)=/g, '');
      } else if (value.startsWith('&lt;=') || value.startsWith('<=')) {
        modifier = 'lte';
        value = value.replace(/(<|&lt;)=/g, '');
      } else if (value.startsWith('&gt;') || value.startsWith('>')) {
        modifier = 'gt';
        value = value.replace(/(>|&gt;)/g, '');
      } else if (value.startsWith('&lt;') || value.startsWith('<')) {
        modifier = 'lt';
        value = value.replace(/(<|&lt;)/g, '');
      } else {
        modifier = 'contains';
      }

      // RegExp
      const regex = CGSearch.regexify({ value, modifier, column });

      // Type
      const type = (this.isNumber(value)) ? 'number' : 'string';

      // tokens.push({ raw: rawValue, value, type, column, modifier, logic, regex });
      tokens.push({ raw: rawValue, value, type, column, columnType, modifier, logic, regex });
    }
    if (this.debug) { console.table(tokens) }
    return tokens;
  }

/**
 * Generates a case-insensitive regular expression based on the provided value,
 * modifier, and optional column restriction.
 *
 * @param {string} value - The string to match. Special regex characters are escaped.
 * @param {string} modifier - Optional match modifier:
 *   - `'begins'`: Match if the value is at the start.
 *   - `'ends'`: Match if the value is at the end.
 *   - `'eq'`: Match exact value.
 *   - Default: No special matching.
 * @param {string} column - Optional column name. If set and not `'any'`, applies stricter matching (anchored to start/end of string).
 *
 * @returns {RegExp} A case-insensitive regular expression constructed from the inputs.
 *
 * @example
 * regexify({ value: 'test', modifier: 'ends', column: 'name' });
 * // => /.*test$/i
 */
  static regexify({ value, modifier, column, columnType }) {
    const escapeRegExp = /[-[\]/{}()*+?.\\^$|]/g;
    const escapedValue = value.replace(escapeRegExp, '\\$&');
    // const columnRestricted = column && column.toLowerCase() !== 'any';
    const columnRestricted = columnType && columnType === 'known';
    let pattern;
    switch (modifier) {
      case 'ends':
        pattern = columnRestricted ? `.*${escapedValue}$` : `.*${escapedValue}(\\s|$)`;
        break;
      case 'begins':
        pattern = columnRestricted ? `^${escapedValue}.*` : `(\\s|^)${escapedValue}.*`;
        break;
      case 'eq':
        pattern = columnRestricted ? `^${escapedValue}$` : `(\\s|^)${escapedValue}(\\s|$)`;
        break;
      default:
        pattern = escapedValue;
    }

    return RegExp(pattern, 'i');
  }

  search(tokens=[]) {
    const data = this.data;
    // console.log(data, tokens)
    const columns = this.columnPaths;
    // console.log('Columns:', columns);
    const startTime = new Date().getTime();
    let filtered = data;
    const sortedTokens = tokens.filter( t => t.raw !== '').sort(this.logicSort);
    if (this.debug) { console.log("Sorted Tokens:"); console.table(sortedTokens) }
    if (sortedTokens.length > 0) {
      let pass, re, testRowString, testParentString, token, logic;
      const isTestRowStringRequired = sortedTokens.some(t => t.columnType === 'any');
      filtered = data.filter( row => {
        pass = false;
        // Create a string of all data values in the row (if required)
        testRowString = isTestRowStringRequired ? this.generateTestString('any', row) : null;
        // testRowString = isTestRowStringRequired ? columns.map(k => this.getValueFor(k, row)).join(' ') : null;
        // testRowString = columns.map( k => this.getValueFor(k, row) ).join(' ');
        // console.log(`Test String: '${testRowString}'`);
        for (token of sortedTokens) {
          re = token.regex;
          logic = token.logic;
          // FIXME: or a parent column (ie meta:)?
          // if (token.column && token.column.toLowerCase() !== 'any') {
          if (token.columnType === 'any') {
            pass = re.test(testRowString);
          } else if (token.columnType === 'parent') {
            testParentString = this.generateTestString(token.column, row);
            pass = re.test(testParentString);
          } else if (token.columnType === 'known') {
            // Checking column value
            const columnValue = this.getValueFor(token.column, row);
            if (token.modifier === 'gt') {
              pass = columnValue > token.value;
            } else if (token.modifier === 'lt') {
              pass = columnValue < token.value;
            } else if (token.modifier === 'gte') {
              pass = columnValue >= token.value;
            } else if (token.modifier === 'lte') {
              pass = columnValue <= token.value;
            } else {
              pass = re.test(columnValue);
            }
          }
          if (logic === 'not') {
            if (pass) {
              pass = false;
              break;
            } else {
              pass = true;
            }
          } else if (logic === 'and') {
            if (!pass) break;
          } else {
            if (pass) break;
          }
        }
        return pass;
      });
    }
    const time = new Date().getTime() - startTime;
    if (this.debug) {
      console.log(`Search [${time} ms] "${tokens.map( t => t.raw).join(' ')}": ${filtered.length} of ${data.length}`);
    }
    return filtered;
  }

  /////////////////////////////////////////////////////////////////////////////
  // HELPERS
  /////////////////////////////////////////////////////////////////////////////
  isNumber(n) {
    return !isNaN(n);
  }

 /**
 * Sorts search items by logical operator priority: NOT > AND > OR.
 *
 * This prioritization improves efficiency by applying the most selective
 * filters first (NOT), then narrowing with AND, and deferring OR (least selective).
 *
 * @param {Object} a - A search item with a `logic` property.
 * @param {Object} b - Another search item with a `logic` property.
 * @returns {number} - Comparison result for sorting.
 */
  logicSort(a, b) {
    const logicKey = { not: 1, and: 2, or: 3}
    return logicKey[a.logic] - logicKey[b.logic];
  }


  // Example usage:
  // const data = [
  //   {name: 'bob',  age: 33},
  //   {name: 'carl', age: 100},
  //   {meta: {name: 'bob', score: 34}, age: 21},
  // ];
  // console.log(getAllColumnPaths(data));
  // Output: [ 'name', 'age', 'meta:name', 'meta:score' ]
  getAllColumnPaths(data, maxDepth = 3) {
    const paths = new Set();

    function visit(obj, prefix = '', depth = 1) {
      if (!obj || typeof obj !== 'object' || depth > maxDepth) return;
      for (const key of Object.keys(obj)) {
        const value = obj[key];
        const path = prefix ? `${prefix}:${key}` : key;
        // if (value && typeof value === 'object') {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          // Add parent path for object columns with trailing colon
          paths.add(path + ':');
          visit(value, path, depth + 1);
        } else {
          // Only add leaf columns
          paths.add(path);
        }
      }
    }
    for (const row of data) {
      visit(row);
    }
    return Array.from(paths);
  }

  /**
   * Returns a Map of columnPath -> type for specified paths.
   * 
   * @param {Array} data - Array of row objects.
   * @param {Array<string>} columnPaths - Paths to check, e.g. ['meta:name', 'age']
   * @param {number} maxDepth - (optional) Maximum depth to search (default 3)
   * @returns {Map<string, string|undefined>} - Map from path to detected type (string, number, boolean, array, null, etc)
   */
  getAllLeafColumnPathsWithTypes(data, columnPaths, maxDepth = 3) {
    const pathTypes = new Map();
    const remainingPaths = new Set(columnPaths);

    for (const row of data) {
      for (const path of remainingPaths) {
        // Only check for type if not already found
        if (!pathTypes.has(path)) {
          const accessor = this.accessors?.[path];
          if (accessor) {
            const value = accessor(row);
            if (value !== undefined) {
              let type = typeof value;
              if (value === null) type = 'null';
              else if (Array.isArray(value)) type = 'array';
              pathTypes.set(path, type);
            }
          }
        }
      }
      // Stop early if all paths are typed
      if (pathTypes.size === columnPaths.length) break;
    }
    // Optionally: for never-found paths, can explicitly set undefined:
    for (const path of columnPaths) {
      if (!pathTypes.has(path)) pathTypes.set(path, undefined);
    }
    return pathTypes;
  }

  _expandAndSetColumns() {
    // Expand any parent paths to include ALL children (leaf and parent paths)
    let expanded = [];
    const allPaths = this.getAllColumnPaths(this._data);

    for (const col of this._columnPaths) {
      if (col.endsWith(':')) {
        expanded.push(col);
        expanded.push(
          ...allPaths.filter(
            p => p.startsWith(col) && p !== col
          )
        );
      } else {
        expanded.push(col);
      }
    }

    this._columnPaths = this.filterExcludedColumns([...new Set(expanded)], this.options.excludeColumns);
    this.accessors = this.makeAccessorMap(this._columnPaths);
  }

  // getAllLeafColumnPathsWithTypes(data, columnPaths = this.columnPaths, maxDepth = 3) {
  // getAllLeafColumnPathsWithTypes(data, maxDepth = 3) {
  //   const MAX_PATH_TYPES = 1000;
  //   const pathTypes = new Map();

  //   function visit(obj, prefix = '', depth = 1) {
  //     if (!obj || typeof obj !== 'object' || depth > maxDepth) return;
  //     for (const key of Object.keys(obj)) {
  //       const value = obj[key];
  //       const path = prefix ? `${prefix}:${key}` : key;

  //       if (value && typeof value === 'object' && !Array.isArray(value)) {
  //         visit(value, path, depth + 1);
  //       } else {
  //         if (!pathTypes.has(path)) {
  //           // Determine value type: prioritize boolean/number/string
  //           let type = typeof value;
  //           if (value === null) {
  //             type = 'null';
  //           } else if (Array.isArray(value)) {
  //             type = 'array';
  //           }
  //           pathTypes.set(path, type);
  //         }
  //       }
  //     }
  //   }

  //   for (const row of data) {
  //     visit(row);
  //     if (pathTypes.size >= MAX_PATH_TYPES) break; // optional
  //   }

  //   return pathTypes
  //   // return Array.from(pathTypes.entries()); // e.g. [['age', 'number'], ['meta:name', 'string']]
  // }

  makeAccessor(path) {
    // If path ends with ':', treat as an object accessor
    if (path.endsWith(':')) {
      const keys = path.slice(0, -1).split(':');
      return function(row) {
        let value = row;
        for (const key of keys) {
          if (value == null) return undefined;
          value = value[key];
        }
        // Only return the object if it is an object, else undefined
        return (value && typeof value === 'object') ? value : undefined;
      };
    } else {
      const keys = path.split(':');
      return function(row) {
        let value = row;
        for (const key of keys) {
          if (value == null) return undefined;
          value = value[key];
        }
        return value;
      };
    }
  }

  // example paths = ['name', 'age', 'meta:name', 'meta:score'];
  makeAccessorMap(paths) {
    const map = {};
    for (const path of paths) {
      map[path] = this.makeAccessor(path);
    }
    return map;
  }

  // Example usage:
  // const row = {meta: {name: 'bob', score: [34, 35]}, age: 21};
  // console.log(getValueFor('meta:score', row)); // "34 35"
  // console.log(getValueFor('meta:name', row));  // "bob"
  // console.log(getValueFor('name', row));       // ""
  getValueFor(path, row, asString = false) {
    const accessor = this.accessors[path];
    if (!accessor) return asString ? '' : undefined;
    let value = accessor(row);
    if (!asString) return value;
    if (Array.isArray(value)) return value.join(' ');
    return value == null ? '' : String(value);
  }

    /**
   * Generates a test string from all relevant column values for a row.
   * @param {string} [parent] - Optional. If 'any' or not provided, use all columns.
   *   If a parent path (e.g. 'meta:'), use only children of that parent.
   * @param {object} row - The data row.
   * @returns {string}
   */
  generateTestString(parent, row) {
  let paths;
  if (!parent || parent === 'any') {
    // Only use leaf columns (skip parent columns ending with ':')
    paths = this.columnPaths.filter(k => !k.endsWith(':'));
  } else {
    // Only use leaf children of the parent (children that don't end with ':')
    paths = this.columnPaths.filter(
      k => k.startsWith(parent) && k !== parent && !k.endsWith(':')
    );
  }
  return paths.map(k => this.getValueFor(k, row)).join(' ');
}
  // generateTestString(parent, row) {
  //   let paths;
  //   if (!parent || parent === 'any') {
  //     paths = this.columnPaths;
  //   } else {
  //     paths = this.columnPaths.filter(k => k.startsWith(parent) && k !== parent);
  //   }
  //   return paths.map(k => this.getValueFor(k, row)).join(' ');
  // }

  hasColumn(path) {
    return this.accessors[path] !== undefined;
  }

  /**
   * Filters and optionally truncates column paths based on a parent and level depth.
   *
   * @param {string[]} allColumnPaths - List of colon-delimited column paths.
   * @param {string} [parent=''] - Optional parent filter (e.g., 'meta:'). '' means top level.
   * @param {number} [levels] - Optional number of levels to include (e.g., 1 for direct children).
   * @returns {string[]} - Filtered and optionally truncated column paths.
   */
  static columnPathsFilter(allColumnPaths, parent = ':', levels) {
    const parentPrefix = (parent === '' || parent === ':') ? '' : parent.endsWith(':') ? parent : parent + ':';
    return Array.from(
      new Set(
        allColumnPaths
          .filter(p => p.startsWith(parentPrefix))
          .map(fullPath => {
            if (fullPath === parentPrefix) return null;
            if (levels === undefined) {
              return fullPath;
            }
            const relative = fullPath.slice(parentPrefix.length);
            const segments = relative.split(':').filter(Boolean);
            const limited = segments.slice(0, levels).join(':');
            const candidate = parentPrefix + limited;
            return allColumnPaths.includes(candidate)
              ? candidate
              : candidate + ':';
          })
          .filter(Boolean)
      )
    );
  }

  // Returns a string representation of the search tokens.
  // stringify() {
  //   let str = '';
  //   for (const token of this._data) {
  //     str += CGSearch.rawifyToken(token) + ' ';
  //   }
  //   return str.trim();
  // }

  static stringifyTokens(tokens) {
    let str = '';
    for (const token of tokens) {
      str += CGSearch.rawifyToken(token) + ' ';
    }
    return str.trim();
  }

    // Return the raw value for a token based on the logic, modifier, and column.
  static rawifyToken(token) {
    let value = token.value;
    let raw = value;

    // Add quotes to raw value if it contains whitespace
    const hasWhitespace = /\s/.test(value);
    if (hasWhitespace) {
      raw = `"${value}"`;
    }

    // Add Modifiers
    switch (token.modifier) {
      case 'begins':
        raw = `${raw}*`; break;
      case 'ends':
        raw = `*${raw}`; break;
      case 'contains':
        raw = `${raw}`; break;
      case 'eq':
        raw = `=${raw}`; break;
      case 'gt':
        raw = `&gt;${raw}`; break;
      case 'gte':
        raw = `&gt;=${raw}`; break;
      case 'lt':
        raw = `&lt;${raw}`; break;
      case 'lte':
        raw = `&lt;=${raw}`; break;
      default:
    }
    // Add Logic
    switch (token.logic) {
      case 'and':
        raw = `+${raw}`; break;
      case 'not':
        raw = `!${raw}`; break;
      default:
    }
    // Add Column
    // if (token.column && token.column !== 'any') {
    if (token.columnType && token.columnType !== 'any') {
      if (token.column.startsWith('?:')) {
        raw = `${raw}:${token.column.slice(2)}`;
      } else {
        raw = `${raw}:${token.column}`;
      }
    }

    return raw;
  }

}

// class Column {
//   /**
//    * Creates a new Column definition.
//    * 
//    * @param {Object} options
//    * @param {string} options.key - Required. Unique identifier for the column.
//    * @param {string} [options.label] - Optional. Display name [Default: `key`]
//    * @param {string} [options.searchType] - Optional. One of 'string','number','bool'. [Default: 'string']
//    */
//   constructor({ key, label = key, searchType = 'string' }) {
//     if (!key) {
//       throw new Error('Column "key" is required.');
//     }

//     const allowed = ['string', 'number', 'bool'];
//     if (!allowed.includes(searchType)) {
//       throw new Error(`Invalid searchType "${searchType}". Must be one of: ${allowed.join(', ')}`);
//     }

//     this.key = key;
//     this.label = label;
//     this.searchType = searchType;
//   }
// }


export default CGSearch;

  // // Example usage:
  // // const columns = ['meta:name', 'meta:score', 'age'];
  // // console.log(getRowStringFromKeys(data[2], columns)); // "bob 34 21"
  // getRowStringFromKeys(row, keys) {
  //   return keys.map(key => {
  //     // Handle nested keys like 'meta:name'
  //     const path = key.split(':');
  //     let value = row;
  //     for (const k of path) {
  //       value = value?.[k];
  //       if (value === undefined) break;
  //     }
  //     return value ?? '';
  //   }).join(' ');
  // }
  // makeAccessors(keys) {
  //   return keys.map(key => {
  //     const path = key.split(':');
  //     return row => path.reduce((acc, k) => acc?.[k], row) ?? '';
  //   });
  // }

  // const accessors = makeAccessors(['meta:name', 'meta:score', 'age']);
  // function getRowString(row) {
  //   return accessors.map(fn => fn(row)).join(' ');
  // }

  // Example usage:
  // console.log(flattenRowValues(data[2])); // "bob 34 21"
  // console.log(flattenRowValues(data[0])); // "bob 33"
  // flattenRowValues(row) {
  //   let values = [];
  //   function helper(val) {
  //     if (val == null) return;
  //     if (typeof val === 'object') {
  //       for (const v of Object.values(val)) {
  //         helper(v);
  //       }
  //     } else {
  //       values.push(String(val));
  //     }
  //   }
  //   helper(row);
  //   return values.join(' ');
  // }